import React, { useState, useEffect } from 'react';
import { getTeamLeads, getOrganizationsByUser, checkResetTeam, setResetTeam } from '../../api';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

function DashboardWelcome({ user, onSectionChange }) {

  // Rollenprüfungen
  const isTeamLeader = user?.isTeamlead || false;
  const isOrgLeader = user?.isOrgLeader || false;
  const isAdmin = user?.isAdmin || false;
  const isCallcenterLeader = user?.isCallcenterLeader || false;
  const isCallcenterMitarbeiter = user?.isCallcenterMitarbeiter || false;
  const isFachberater = user?.isFachberater || false;
  const isAgenturLeader = user?.isAgenturLeader || false;
  const isNormalMitarbeiter = user?.role_type === 0;

  const navigate = useNavigate();
  
  // State für Organisationen
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Organisationen laden für Organisationsleiter
  useEffect(() => {
    const loadOrganizations = async () => {
      if (isOrgLeader && user?.id) {
        setLoading(true);
        try {
          const response = await getOrganizationsByUser(user.id);
          if (response.success) {
            setOrganizations(response.organizations || []);
          } else {
            console.error('Fehler beim Laden der Organisationen:', response.error);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Organisationen:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadOrganizations();
  }, [isOrgLeader, user?.id]);

  // Dynamischer Titel je nach Organisation
  let orgTitle = 'Bundesenergie Nord';
  let orgTitleStyle = {};
  if (user?.organization_id === 6) {
    orgTitle = 'Energie +';
    orgTitleStyle = { color: '#0f437c' };
  }

  // Hilfsfunktion für ResetTeam-Check und ggf. Logout
  const handleLeadButtonClick = async (type) => {
    if (!user?.id) return;
    // Prüfe ResetTeam-Status
    const res = await checkResetTeam(user.id);

    if (res.success && typeof res.resetTeam !== 'undefined') {
      if (res.resetTeam === 0) {
        // Setze ResetTeam auf 1
        await setResetTeam(user.id);
        // Session/Token löschen
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('sessionData');
        // Weiterleitung zur Login-Seite
        window.location.href = '/login';
        return;
      } else if (res.resetTeam === 1) {
        // Weiterleitung zum jeweiligen Lead-Formular
        if (type === 'pv') {
          navigate('/add-pv-lead');
        } else if (type === 'wp') {
          navigate('/add-wp-lead');
        } else if (type === 'pv+wp') {
          navigate('/add-pv-wp-lead');
        }
        return;
      }
    } else {
      alert('Fehler beim Prüfen des ResetTeam-Status!');
    }
  };

  return (
    <div className="welcome-screen">
      <h1>Willkommen bei der<br />
        <span style={orgTitleStyle}>{orgTitle}</span>
      </h1>
      
      {/* Schnellzugriff für Organisationsleiter - Pro Organisation */}
      {isOrgLeader && (
        <div className="organizations-quick-access">
          <h3>Schnellzugriff</h3>
          {loading ? (
            <div className="loading-organizations">
              <div className="loading-spinner"></div>
              <span>Lade Organisationen...</span>
            </div>
          ) : organizations.length > 0 ? (
            <div className="organizations-grid">
              {organizations.map((org) => (
                <div key={org.id} className="organization-quick-access-card">
                  <h4 className="org-name">🏢 {org.name}</h4>
                  <div className="quick-action-buttons">
                    <button   
                      className="quick-action-btn"
                      onClick={() => navigate(`/org-dashboard/${org.id}`)}
                    >
                      📊 Organisations Dashboard
                    </button>
                    <button 
                      className="quick-action-btn"
                      onClick={() => navigate(`/organization-management/${org.id}`)}
                    >
                      👨‍👩‍👧‍👦 Meine Teams
                    </button>
                    <button
                      className="quick-action-btn"
                      onClick={() => navigate(`/agenturen?orgId=${org.id}`)}
                    >
                      💼 Meine Agenturen
                    </button>
                    <button 
                      className="quick-action-btn"
                      onClick={() => navigate(`/organization-members/${org.id}`)}
                    >
                      👤 Mitglieder verwalten
                    </button>
                    <button 
                      className="quick-action-btn"
                      onClick={() => navigate(`/organization-leads/${org.id}`)}
                    >
                      📋 Alle Organisations-Leads
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-organizations">
              <p>Keine Organisationen verfügbar</p>
            </div>
          )}
        </div>
      )}

      {/* Schnellzugriff für Fachberater */}
      {isFachberater && (
        <div className="fachberater-quick-actions">
          <h3>Schnellzugriff</h3>
          <div className="quick-action-buttons">
            <button 
              className="quick-action-btn"
              onClick={() => navigate('/termine')}
            >
              📅 Meine Termine
            </button>
          </div>
        </div>
      )}

      {/* Schnellzugriff für normale Mitarbeiter */}
      {isNormalMitarbeiter && (
        <div className="mitarbeiter-quick-actions">
          <h3>Schnellzugriff</h3>
          <div className="quick-action-buttons">
            <button 
              className="quick-action-btn"
              onClick={() => handleLeadButtonClick('pv')}
            >
              ☀️ PV-Lead erstellen
            </button>
            <button 
              className="quick-action-btn"
              onClick={() => handleLeadButtonClick('wp')}
            >
              🔥 WP-Lead erstellen
            </button>
            <button 
              className="quick-action-btn"
              onClick={() => handleLeadButtonClick('pv+wp')}
            >
              PV & WP Lead erstellen
            </button>
          </div>
        </div>
      )}

      {/* Schnellzugriff für Teamleiter */}
      {isTeamLeader && (
        <div className="teamlead-quick-actions">
          <h3>Schnellzugriff für Teamleiter</h3>
          <div className="quick-action-buttons">
            {/* Erste Reihe: Lead-Buttons */}
            <div className="button-row">
              <button 
                className="quick-action-btn"
                onClick={() => handleLeadButtonClick('pv')}
              >
                ☀️ PV-Lead erstellen
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => handleLeadButtonClick('wp')}
              >
                🔥 WP-Lead erstellen
              </button>
              <button 
                className="quick-action-btn"
                onClick={() => handleLeadButtonClick('pv+wp')}
              >
                PV & WP Lead erstellen
              </button>
            </div>
            {/* Zweite Reihe: Team-Management-Buttons */}
            <div className="button-row">
              {user?.agentur_id && user?.a_team_id ? (
                <>
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate(`/agenturen/${user.agentur_id}/teams/${user.a_team_id}/dashboard`)}
                  >
                    📊 Team Dashboard
                  </button>
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate(`/agenturen/${user.agentur_id}/teams/${user.a_team_id}/mitarbeiter`)}
                  >
                    👥 Mitglieder ansehen
                  </button>
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate(`/agenturen/${user.agentur_id}/teams/${user.a_team_id}/leads`)}
                  >
                    📋 Team-Leads ansehen
                  </button>
                </>
              ) : (
                <div style={{ color: 'red', padding: '8px 16px', fontWeight: 'bold' }}>
                  Team- oder Agentur-ID fehlt. Bitte kontaktieren Sie den Administrator.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Schnellzugriff für Agenturleiter */}
      {isAgenturLeader && (
        <div className="teamlead-quick-actions">

          <h3>Schnellzugriff</h3>

          <div className="quick-action-grid">
            {/* Linke Spalte - Lead Erstellung */}
            <div className="quick-action-column">
              <h4>Lead Erstellung</h4>
              <div className="quick-action-buttons">
                <button 
                  className="quick-action-btn lead-btn"
                  onClick={() => handleLeadButtonClick('pv')}
                >
                  ⚡ PV Lead erstellen
                </button>
                <button 
                  className="quick-action-btn lead-btn"
                  onClick={() => handleLeadButtonClick('wp')}
                >
                  🌡️ WP Lead erstellen
                </button>
                <button 
                  className="quick-action-btn lead-btn"
                  onClick={() => handleLeadButtonClick('pv+wp')}
                >
                  PV & WP Lead erstellen
                </button>
              </div>
            </div>

            {/* Rechte Spalte - Navigation */}
            <div className="quick-action-column">
              <h4>Navigation</h4>
              <div className="quick-action-buttons">
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate(`/agentur-dashboard/${user.agentur_id}`)}
                >
                  📊 Agentur Dashboard
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate(`/agenturen/${user.agentur_id}/mitglieder`)}
                >
                  👥 Mitglieder verwalten
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate(`/agenturen/${user.agentur_id}/leads`)}
                >
                  📋 Alle Leads ansehen
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate(`/agenturen/${user.agentur_id}/teams`)}
                >
                  👥 Meine Teams ansehen
                </button>
              </div>
            </div>
          </div>

        </div>
        
      )}

      <div className="dashboard-grid">
        {isOrgLeader && (
          <Link to="/termine" className="dashboard-card">
            <span className="card-icon">📅</span>
            <h2 className="card-title">Termine</h2>
            <p className="card-description">Verwalten hier alle Termine</p>
          </Link>
        )}
        {(isCallcenterLeader || isCallcenterMitarbeiter) && (
          <>
            <Link to="/kunden" className="dashboard-card">
              <span className="card-icon">👥</span>
              <h2 className="card-title">Kunden</h2>
              <p className="card-description">Gelange direkt zu allen Kunden</p>
            </Link>
            <Link to="/kunden-nicht-erreicht" className="dashboard-card">
              <span className="card-icon">❌</span>
              <h2 className="card-title">Nicht erreichte Kunden</h2>
              <p className="card-description">Alle Kunden die nicht erreicht wurden</p>
            </Link>
          </>
        )}
        {isOrgLeader && (
          <>
            <button type="button" className="dashboard-card dashboard-card-button" onClick={() => handleLeadButtonClick('pv')}>
              <span className="card-icon">☀️</span>
              <h2 className="card-title">PV-Lead erstellen</h2>
              <p className="card-description">Neuen PV-Lead anlegen</p>
            </button>
            <button type="button" className="dashboard-card dashboard-card-button" onClick={() => handleLeadButtonClick('wp')}>
              <span className="card-icon">🔥</span>
              <h2 className="card-title">WP-Lead erstellen</h2>
              <p className="card-description">Neuen WP-Lead anlegen</p>
            </button>
            <button type="button" className="dashboard-card dashboard-card-button" onClick={() => handleLeadButtonClick('pv+wp')}>
              <span className="card-icon">⚡</span>
              <h2 className="card-title">PV & WP Lead erstellen</h2>
              <p className="card-description">Kombinierten Lead anlegen</p>
            </button>
          </>
        )}
      </div>

      <style jsx>{`
        .welcome-screen {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
        }

        .welcome-screen h1 {
          font-size: 2rem;
          color: #2d3748;
          margin-bottom: 2rem;
          text-align: center;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .dashboard-card {
          background: white;
          border-radius: 0.5rem;
          padding: 1.25rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: pointer;
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .dashboard-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .dashboard-card-button {
          border: none;
          font: inherit;
          width: 100%;
        }

        .card-icon {
          font-size: 2rem;
          margin-bottom: 0.75rem;
        }

        .card-title {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .card-description {
          font-size: 0.875rem;
          color: #718096;
          line-height: 1.4;
        }

        /* Neue Styles für Organisations-Schnellzugriff */
        .organizations-quick-access {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin: 2rem auto;
          max-width: 1200px;
        }

        .organizations-quick-access h3 {
          color: #2d3748;
          margin-bottom: 1.5rem;
          font-size: 1.25rem;
          text-align: center;
        }

        .organizations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, 350px);
          gap: 1.5rem;
          justify-content: center;
        }

        .organization-quick-access-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .organization-quick-access-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .org-name {
          color: #2d3748;
          margin: 0 0 1rem 0;
          font-size: 1.1rem;
          font-weight: 600;
          text-align: center;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .loading-organizations {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          color: #718096;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #4a6de5;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .no-organizations {
          text-align: center;
          color: #718096;
          padding: 2rem;
          font-style: italic;
        }

        .fachberater-quick-actions {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin: 2rem auto;
          max-width: 400px;
        }

        .fachberater-quick-actions h3 {
          color: #2d3748;
          margin-bottom: 1rem;
          font-size: 1.25rem;
        }

        .quick-action-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .quick-action-btn {
          background: #4a6de5;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 12px 24px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .quick-action-btn:hover {
          background: #3c5ce0;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(74, 109, 229, 0.3);
        }

        .mitarbeiter-quick-actions {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin: 2rem auto;
          max-width: 400px;
        }

        .mitarbeiter-quick-actions h3 {
          color: #2d3748;
          margin-bottom: 1rem;
          font-size: 1.25rem;
        }

        /* Teamleiter Schnellzugriff Styles */
        .teamlead-quick-actions {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin: 2rem auto;
          max-width: 1000px;
        }

        .teamlead-quick-actions h3 {
          color: #2d3748;
          margin-bottom: 1.5rem;
          font-size: 1.25rem;
        }

        .quick-action-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        .quick-action-column {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .quick-action-column h4 {
          color: #4a5568;
          margin-bottom: 1rem;
          font-size: 1.1rem;
          font-weight: 600;
          text-align: center;
        }

        .lead-btn {
          background: #38a169 !important;
          min-width: 200px;
          white-space: nowrap;
        }

        .lead-btn:hover {
          background: #2f855a !important;
        }
          text-align: center;
        }

        .teamlead-quick-actions .quick-action-buttons {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          align-items: center;
        }

        .teamlead-quick-actions .button-row {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
          justify-content: center;
          width: 100%;
        }

        .teamlead-quick-actions .quick-action-btn {
          min-width: 160px;
          max-width: 200px;
          flex: 1;
        }

        /* Spezielle Farben für Teamleiter-Buttons */
        .teamlead-quick-actions .quick-action-btn:nth-child(3) {
          background: #38a169; /* Grün für Team Dashboard */
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(3):hover {
          background: #2f855a;
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(2) {
          background: #d69e2e; /* Orange für Team verwalten */
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(2):hover {
          background: #b7791f;
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(5) {
          background: #e53e3e; /* Rot für Mitarbeiter hinzufügen */
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(5):hover {
          background: #c53030;
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(6) {
          background: #805ad5; /* Lila für Team-Leads ansehen */
        }

        .teamlead-quick-actions .quick-action-btn:nth-child(6):hover {
          background: #6b46c1;
        }

        @media (max-width: 768px) {
          .welcome-screen {
            padding: 1rem;
          }

          .dashboard-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .organizations-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .welcome-screen h1 {
            font-size: 1.5rem;
          }

          .organizations-quick-access {
            padding: 1.5rem;
          }

          .organization-quick-access-card {
            padding: 1rem;
          }

          .teamlead-quick-actions {
            padding: 1.5rem;
            max-width: 100%;
          }

          .teamlead-quick-actions .quick-action-buttons {
            gap: 1rem;
          }

          .teamlead-quick-actions .button-row {
            flex-direction: column;
            gap: 1rem;
          }

          .teamlead-quick-actions .quick-action-btn {
            max-width: 100%;
            min-width: auto;
          }
        }
      `}</style>
      
    </div>
  );
}

export default DashboardWelcome;